import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { BRAND } from "src/theme/brandTokens";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { splitMontoMoneda } from "src/utils/splitMontoMonedaCliente";
import { formatFechaPortada } from "src/utils/propuestaCoverFormat";
import {
  FRECUENCIA_PLAN_LABEL,
  esCobroPorTrabajador,
  formatearCaptionItem,
  modalidadItem,
  MODALIDAD_META,
  ORDEN_MODALIDAD,
} from "src/utils/propuestaItemLabels";
import ItemDecisionControl from "./ItemDecisionControl";

// ─── Helpers ────────────────────────────────────────────────────────────────

function padNumero(n) {
  return String(n).padStart(2, "0");
}

function estilosPorEstado(decision) {
  if (decision === "ACEPTADO") {
    return {
      border: `1px solid ${BRAND.color.primary}`,
      bgcolor: "#fffaf3",
    };
  }
  if (decision === "RECHAZADO") {
    return {
      border: `1px solid ${BRAND.color.divider}`,
      bgcolor: BRAND.color.grey10,
    };
  }
  return {
    border: `1px solid ${BRAND.color.divider}`,
    bgcolor: BRAND.color.paper,
  };
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

const NumeroCuadrado = ({ numero }) => (
  <Box
    aria-hidden
    sx={{
      flexShrink: 0,
      width: "32pt",
      height: "32pt",
      bgcolor: BRAND.color.primary,
      color: BRAND.color.paper,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: BRAND.font.family.display,
      fontSize: BRAND.font.size.bodyLarge,
      fontWeight: BRAND.font.weight.black,
      letterSpacing: 0,
    }}
  >
    {padNumero(numero)}
  </Box>
);

const FaseBlockEyebrow = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.primary,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.widest,
      fontWeight: BRAND.font.weight.medium,
      mb: "3mm",
    }}
  >
    {children}
  </Box>
);

const FaseBlockDivider = () => (
  <Box
    component="hr"
    sx={{
      border: 0,
      borderTop: `1px solid ${BRAND.color.divider}`,
      m: "4mm 0",
    }}
  />
);

/**
 * Lista de actividades con bullets cuadrados naranja, una entrada por línea
 * (los usuarios separan con `\n` al ingresar el campo).
 */
const ActividadesLista = ({ texto }) => {
  const items = (texto || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length === 0) return null;
  return (
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
      {items.map((act, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            position: "relative",
            pl: "12pt",
            mb: "2mm",
            lineHeight: BRAND.font.lineHeight.body,
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "0.55em",
              width: "5pt",
              height: "5pt",
              bgcolor: BRAND.color.primary,
            },
            "&:last-child": { mb: 0 },
          }}
        >
          {act}
        </Box>
      ))}
    </Box>
  );
};

const FaseBlockBody = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.body,
      color: BRAND.color.inkSoft,
      lineHeight: BRAND.font.lineHeight.body,
      whiteSpace: "pre-wrap",
    }}
  >
    {children}
  </Box>
);

const FooterEyebrow = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.grey50,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.widest,
      fontWeight: BRAND.font.weight.medium,
      mb: "1mm",
    }}
  >
    {children}
  </Box>
);

const ItemCard = ({ numero, item, decision, onChangeDecision, readOnly }) => {
  const eyebrowFase = `Fase ${padNumero(numero)}`;
  const captionTipo = formatearCaptionItem(item);
  const titulo = item.nombreServicio || item.faseTitulo || "Servicio";
  const { monto, sigla } = splitMontoMoneda(
    item.valorConDescuento,
    item.tipoMoneda,
  );
  // Un plan que se cobra por trabajador lleva la unidad junto a la moneda: el
  // cliente acepta o rechaza el servicio desde esta tarjeta, y sin ella el
  // valor se lee como un monto fijo.
  const porTrabajador =
    item.tipoItem === "PLAN" && esCobroPorTrabajador(item.refTipoPlan);
  const conUnidad = (s) =>
    porTrabajador ? (s ? `${s} / trabajador` : "/ trabajador") : s;
  const siglaMonto = conUnidad(sigla);
  // Descuento explícito: mostrar valor original tachado, chip de porcentaje
  // y caption de vencimiento del descuento. Espejo del render del PDF en
  // `buildProposalHTMLv2`.
  const descuentoNum = Number(item.descuento) || 0;
  const valorOriginalNum = Number(item.valorOriginal) || 0;
  const tieneDescuento =
    descuentoNum > 0 &&
    valorOriginalNum > 0 &&
    valorOriginalNum > Number(item.valorConDescuento);
  const montoOriginalFmt = tieneDescuento
    ? splitMontoMoneda(item.valorOriginal, item.tipoMoneda)
    : null;
  const siglaOriginal = montoOriginalFmt
    ? conUnidad(montoOriginalFmt.sigla)
    : "";
  const descuentoChipTxt = tieneDescuento
    ? `-${descuentoNum % 1 === 0 ? descuentoNum : descuentoNum.toFixed(2).replace(/\.?0+$/, "")}% off`
    : "";
  const venceTxt =
    tieneDescuento && item.fechaTerminoDesc
      ? `Vence ${formatFechaPortada(item.fechaTerminoDesc)}`
      : "";
  const tienePanelFase = !!(
    item.faseObjetivo ||
    item.faseActividades ||
    item.faseEntregable
  );
  const estilo = estilosPorEstado(decision?.decision);
  // Cuando el item está rechazado se atenúa solo la zona informativa
  // (header + panel de fase + footer Frecuencia/Fecha). El control de
  // decisión y el motivo opcional viven fuera del wrapper atenuado para
  // que el cliente note que aún puede escribir el motivo.
  const atenuarInformativa = decision?.decision === "RECHAZADO";

  return (
    <Box
      sx={{
        ...estilo,
        p: { xs: 2.5, md: 3 },
        mb: 3,
        transition: "background-color 200ms ease, border-color 200ms ease",
        fontFamily: BRAND.font.family.base,
      }}
    >
      <Box
        sx={{
          opacity: atenuarInformativa ? 0.55 : 1,
          transition: "opacity 200ms ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: { xs: 2, md: 3 },
          }}
        >
          <NumeroCuadrado numero={numero} />
          <Box sx={{ flex: 1, minWidth: 0, pt: "2pt" }}>
            <Box
              sx={{
                fontSize: BRAND.font.size.eyebrow,
                color: BRAND.color.grey50,
                textTransform: "uppercase",
                letterSpacing: BRAND.font.letterSpacing.widest,
                fontWeight: BRAND.font.weight.medium,
                mb: "1mm",
              }}
            >
              {eyebrowFase}
            </Box>
            <Box
              component="h3"
              sx={{
                m: 0,
                fontFamily: BRAND.font.family.display,
                fontSize: BRAND.font.size.h3,
                fontWeight: BRAND.font.weight.black,
                color: BRAND.color.inkSoft,
                lineHeight: BRAND.font.lineHeight.snug,
              }}
            >
              {titulo}
            </Box>
            <Box
              sx={{
                mt: "1mm",
                fontFamily: BRAND.font.family.base,
                fontSize: BRAND.font.size.bodySmall,
                fontWeight: BRAND.font.weight.light,
                color: BRAND.color.grey70,
                lineHeight: BRAND.font.lineHeight.snug,
              }}
            >
              {captionTipo}
            </Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              textAlign: "right",
              pt: "2pt",
            }}
          >
            {montoOriginalFmt && (
              <Box
                sx={{
                  fontFamily: BRAND.font.family.base,
                  fontSize: BRAND.font.size.bodySmall,
                  fontWeight: BRAND.font.weight.regular,
                  color: BRAND.color.grey50,
                  textDecoration: "line-through",
                  lineHeight: BRAND.font.lineHeight.tight,
                  whiteSpace: "nowrap",
                  mb: "1mm",
                }}
              >
                {siglaOriginal
                  ? `${montoOriginalFmt.monto} ${siglaOriginal}`
                  : montoOriginalFmt.monto}
              </Box>
            )}
            <Box
              sx={{
                fontFamily: BRAND.font.family.display,
                fontSize: BRAND.font.size.h3,
                fontWeight: BRAND.font.weight.black,
                color: BRAND.color.inkSoft,
                lineHeight: BRAND.font.lineHeight.tight,
                whiteSpace: "nowrap",
              }}
            >
              {monto}
            </Box>
            {siglaMonto && (
              <Box
                sx={{
                  fontSize: BRAND.font.size.eyebrow,
                  fontWeight: BRAND.font.weight.bold,
                  color: BRAND.color.primary,
                  letterSpacing: BRAND.font.letterSpacing.wide,
                  mt: "1mm",
                }}
              >
                {siglaMonto}
              </Box>
            )}
            {descuentoChipTxt && (
              <Box
                sx={{
                  display: "inline-block",
                  mt: "1.5mm",
                  px: "2mm",
                  py: "0.5mm",
                  fontSize: BRAND.font.size.eyebrow,
                  fontWeight: BRAND.font.weight.bold,
                  textTransform: "uppercase",
                  letterSpacing: BRAND.font.letterSpacing.wide,
                  color: BRAND.color.primary,
                  bgcolor: BRAND.color.primaryTint,
                  border: `1px solid ${BRAND.color.primary}`,
                  borderRadius: "2mm",
                  whiteSpace: "nowrap",
                }}
              >
                {descuentoChipTxt}
              </Box>
            )}
            {venceTxt && (
              <Box
                sx={{
                  mt: "1mm",
                  fontSize: BRAND.font.size.eyebrow,
                  color: BRAND.color.grey70,
                  letterSpacing: BRAND.font.letterSpacing.wide,
                  whiteSpace: "nowrap",
                }}
              >
                {venceTxt}
              </Box>
            )}
          </Box>
        </Box>

        {tienePanelFase && (
          <Box sx={{ mt: "6mm" }}>
            {item.faseObjetivo && (
              <>
                <FaseBlockDivider />
                <FaseBlockEyebrow>Objetivo</FaseBlockEyebrow>
                <FaseBlockBody>{item.faseObjetivo}</FaseBlockBody>
              </>
            )}
            {item.faseActividades && (
              <>
                <FaseBlockDivider />
                <FaseBlockEyebrow>Actividades</FaseBlockEyebrow>
                <ActividadesLista texto={item.faseActividades} />
              </>
            )}
            {item.faseEntregable && (
              <>
                <FaseBlockDivider />
                <FaseBlockEyebrow>Entregable</FaseBlockEyebrow>
                <FaseBlockBody>{item.faseEntregable}</FaseBlockBody>
              </>
            )}
          </Box>
        )}

        {(() => {
          const frecuenciaLabel =
            item.tipoItem === "PLAN"
              ? FRECUENCIA_PLAN_LABEL[item.tipoFrecuencia]
              : null;
          const fechaEntrega =
            item.tipoItem !== "PLAN" ? item.fechaLimite : null;
          if (!frecuenciaLabel && !fechaEntrega) return null;
          return (
            <Box
              sx={{
                display: "flex",
                gap: { xs: 4, md: 8 },
                flexWrap: "wrap",
                borderTop: `1px solid ${BRAND.color.divider}`,
                pt: "4mm",
                mt: "4mm",
              }}
            >
              {frecuenciaLabel && (
                <Box>
                  <FooterEyebrow>Frecuencia</FooterEyebrow>
                  <Box
                    sx={{
                      fontSize: BRAND.font.size.bodyLarge,
                      fontWeight: BRAND.font.weight.medium,
                      color: BRAND.color.inkSoft,
                      lineHeight: BRAND.font.lineHeight.tight,
                    }}
                  >
                    {frecuenciaLabel}
                  </Box>
                </Box>
              )}
              {fechaEntrega && (
                <Box>
                  <FooterEyebrow>Fecha de entrega</FooterEyebrow>
                  <Box
                    sx={{
                      fontSize: BRAND.font.size.bodyLarge,
                      fontWeight: BRAND.font.weight.medium,
                      color: BRAND.color.inkSoft,
                      lineHeight: BRAND.font.lineHeight.tight,
                    }}
                  >
                    {formatFechaPortada(fechaEntrega)}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${BRAND.color.divider}`,
          pt: "4mm",
          mt: "4mm",
        }}
      >
        <ItemDecisionControl
          decision={decision?.decision}
          motivo={decision?.motivo}
          onChange={({ decision: d, motivo: m }) =>
            onChangeDecision(item.id, { decision: d, motivo: m })
          }
          readOnly={readOnly}
          respuestaPrevia={item.respuesta}
        />
      </Box>
    </Box>
  );
};

const EmpresaHeader = ({ nombre, rut, cantidadServicios }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      flexWrap: "wrap",
      gap: 2,
      borderBottom: `1px solid ${BRAND.color.divider}`,
      pb: "4mm",
      mb: "8mm",
    }}
  >
    <Box
      sx={{
        fontFamily: BRAND.font.family.display,
        fontSize: BRAND.font.size.bodyLarge,
        fontWeight: BRAND.font.weight.bold,
        color: BRAND.color.inkSoft,
      }}
    >
      {nombre}
    </Box>
    {rut && (
      <Box
        sx={{
          fontSize: BRAND.font.size.eyebrow,
          color: BRAND.color.grey70,
          letterSpacing: BRAND.font.letterSpacing.wide,
        }}
      >
        {rut}
      </Box>
    )}
    <Box sx={{ flexGrow: 1 }} />
    <Box
      sx={{
        fontSize: BRAND.font.size.eyebrow,
        color: BRAND.color.primary,
        textTransform: "uppercase",
        letterSpacing: BRAND.font.letterSpacing.widest,
        fontWeight: BRAND.font.weight.medium,
      }}
    >
      {cantidadServicios} {cantidadServicios === 1 ? "servicio" : "servicios"}
    </Box>
  </Box>
);

const SubtotalRow = ({ nombre, subtotales }) => {
  const lineas = ORDEN_MODALIDAD.filter((clave) => subtotales[clave]).flatMap(
    (clave) => {
      const meta = MODALIDAD_META[clave];
      return Object.entries(subtotales[clave]).map(([m, v]) => {
        const { monto, sigla } = splitMontoMoneda(v, m);
        const unidad = meta.unidad ? ` ${meta.unidad}` : "";
        return `${meta.subtotal}: ${monto} ${sigla}${unidad}`;
      });
    },
  );
  return (
    <Box
      sx={{
        textAlign: "right",
        fontSize: BRAND.font.size.eyebrow,
        color: BRAND.color.grey70,
        textTransform: "uppercase",
        letterSpacing: BRAND.font.letterSpacing.wide,
      }}
    >
      Subtotal {nombre && <>{nombre} </>}
      <Box
        component="strong"
        sx={{
          color: BRAND.color.inkSoft,
          fontWeight: BRAND.font.weight.bold,
          letterSpacing: 0,
          textTransform: "none",
          fontSize: BRAND.font.size.body,
        }}
      >
        {lineas.join(" · ")}
      </Box>
    </Box>
  );
};

const IndiceEmpresas = ({ grupos, onClick }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: 1.5,
      alignItems: "center",
      mb: "8mm",
    }}
  >
    <Box
      sx={{
        fontSize: BRAND.font.size.eyebrow,
        color: BRAND.color.grey50,
        textTransform: "uppercase",
        letterSpacing: BRAND.font.letterSpacing.widest,
        fontWeight: BRAND.font.weight.medium,
      }}
    >
      Ir a
    </Box>
    {grupos.map((g) => (
      <Box
        key={g.rut}
        component="button"
        type="button"
        onClick={() => onClick(g.rut)}
        sx={{
          appearance: "none",
          border: `1px solid ${BRAND.color.divider}`,
          bgcolor: BRAND.color.paper,
          color: BRAND.color.inkSoft,
          fontFamily: BRAND.font.family.base,
          fontSize: BRAND.font.size.eyebrow,
          fontWeight: BRAND.font.weight.medium,
          letterSpacing: BRAND.font.letterSpacing.wide,
          textTransform: "uppercase",
          px: 1.5,
          py: 0.75,
          cursor: "pointer",
          transition: "background-color 150ms ease, border-color 150ms ease",
          "&:hover": {
            borderColor: BRAND.color.primary,
            bgcolor: "#fffaf3",
          },
        }}
      >
        {g.empresa.nombre}
      </Box>
    ))}
  </Box>
);

// ─── Componente principal ───────────────────────────────────────────────────

const ItemsByCompanyV2 = ({
  items,
  empresas,
  decisions,
  onChangeDecision,
  readOnly,
}) => {
  const blockRefs = useRef({});

  // Agrupación por refCliente preservando orden de aparición — lógica intacta.
  const grupos = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.refCliente || "__sin_empresa__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries()).map(([rut, itemsEmpresa]) => ({
      rut,
      empresa: (empresas || []).find((e) => e.rut === rut) || {
        rut,
        nombre: rut,
      },
      items: itemsEmpresa,
    }));
  }, [items, empresas]);

  const scrollToEmpresa = (rut) => {
    const el = blockRefs.current[rut];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const mostrarIndice = grupos.length >= 2;
  const mostrarSubtotalesPorEmpresa = grupos.length >= 2;

  return (
    <Box sx={{ fontFamily: BRAND.font.family.base }}>
      {mostrarIndice && (
        <IndiceEmpresas grupos={grupos} onClick={scrollToEmpresa} />
      )}

      {grupos.map((g, gi) => {
        // La numeración es global y contigua por empresa, no reinicia en cada
        // una: el PDF y la vista interna numeran así, y un "Fase 01" que en el
        // portal apunta a un servicio distinto del que nombra el documento deja
        // al vendedor y al cliente hablando de cosas distintas.
        const offsetNumero = grupos
          .slice(0, gi)
          .reduce((acc, x) => acc + x.items.length, 0);
        const subtotales = g.items.reduce((acc, it) => {
          if (!(Number(it.valorConDescuento || 0) > 0)) return acc;
          const clave = modalidadItem(it);
          const m = formatearMonedaCliente(it.tipoMoneda) || "CLP";
          acc[clave] = acc[clave] || {};
          acc[clave][m] =
            (acc[clave][m] || 0) + Number(it.valorConDescuento || 0);
          return acc;
        }, {});

        return (
          <Box
            key={g.rut}
            ref={(el) => {
              blockRefs.current[g.rut] = el;
            }}
            sx={{ mb: { xs: 6, md: 8 }, scrollMarginTop: "80px" }}
          >
            {mostrarSubtotalesPorEmpresa && (
              <EmpresaHeader
                nombre={g.empresa.nombre}
                rut={g.empresa.rut}
                cantidadServicios={g.items.length}
              />
            )}

            {g.items.map((it, idx) => (
              <ItemCard
                key={it.id}
                numero={offsetNumero + idx + 1}
                item={it}
                decision={decisions?.[it.id]}
                onChangeDecision={onChangeDecision}
                readOnly={readOnly}
              />
            ))}

            {mostrarSubtotalesPorEmpresa && (
              <SubtotalRow nombre={g.empresa.nombre} subtotales={subtotales} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ─── PropTypes ──────────────────────────────────────────────────────────────

NumeroCuadrado.propTypes = {
  numero: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

FaseBlockEyebrow.propTypes = { children: PropTypes.node.isRequired };
FaseBlockBody.propTypes = { children: PropTypes.node.isRequired };
FooterEyebrow.propTypes = { children: PropTypes.node.isRequired };

ActividadesLista.propTypes = { texto: PropTypes.string };

ItemCard.propTypes = {
  numero: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  decision: PropTypes.shape({
    decision: PropTypes.string,
    motivo: PropTypes.string,
  }),
  onChangeDecision: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

EmpresaHeader.propTypes = {
  nombre: PropTypes.string,
  rut: PropTypes.string,
  cantidadServicios: PropTypes.number.isRequired,
};

SubtotalRow.propTypes = {
  nombre: PropTypes.string,
  subtotales: PropTypes.object.isRequired,
};

IndiceEmpresas.propTypes = {
  grupos: PropTypes.array.isRequired,
  onClick: PropTypes.func.isRequired,
};

ItemsByCompanyV2.propTypes = {
  items: PropTypes.array.isRequired,
  empresas: PropTypes.array,
  decisions: PropTypes.object,
  onChangeDecision: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

ItemsByCompanyV2.defaultProps = {
  empresas: [],
  decisions: {},
  readOnly: false,
};

export default ItemsByCompanyV2;
